import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    listSubdomains, deleteSubdomain, checkSubdomainSsl,
} from '@/services/api/chatbot';
import type { ChatbotSubdomain } from '@/types/chatbot';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Plus, ShieldCheck, ShieldAlert, RefreshCw, Trash2, ArrowRight, Search, ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import SubdomainFormModal from './components/SubdomainFormModal';
import DnsInstructions from './components/DnsInstructions';

function sslBadge(status: ChatbotSubdomain['ssl_status']) {
    if (status === 'valid')    return <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200"><ShieldCheck className="w-3 h-3 mr-1" />SSL valido</Badge>;
    if (status === 'pending')  return <Badge className="bg-amber-100 text-amber-700 border border-amber-200">SSL pendente</Badge>;
    if (status === 'invalid')  return <Badge className="bg-red-100 text-red-700 border border-red-200"><ShieldAlert className="w-3 h-3 mr-1" />SSL invalido</Badge>;
    return <Badge className="bg-slate-100 text-slate-700 border border-slate-200"><ShieldAlert className="w-3 h-3 mr-1" />SSL erro</Badge>;
}

export default function ChatbotSubdomains() {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState<ChatbotSubdomain | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<ChatbotSubdomain | null>(null);
    const [search, setSearch] = useState('');
    const [expandedDns, setExpandedDns] = useState<Set<number>>(new Set());

    const toggleDns = (id: number) => {
        setExpandedDns(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const { data, isLoading } = useQuery({
        queryKey: ['chatbot-subdomains'],
        queryFn: () => listSubdomains(),
    });

    const deleteMut = useMutation({
        mutationFn: (id: number) => deleteSubdomain(id),
        onSuccess: () => {
            toast({ title: 'Subdominio deletado!' });
            queryClient.invalidateQueries({ queryKey: ['chatbot-subdomains'] });
            setDeleteConfirm(null);
        },
        onError: (e: any) => toast({
            variant: 'destructive', title: 'Erro ao deletar',
            description: e.response?.data?.detail || e.message,
        }),
    });

    const sslMut = useMutation({
        mutationFn: (id: number) => checkSubdomainSsl(id),
        onSuccess: (sub) => {
            toast({
                title: 'SSL verificado',
                description: `${sub.subdomain}: ${sub.ssl_status}${sub.ssl_expires_at ? ' (expira ' + sub.ssl_expires_at + ')' : ''}`,
            });
            queryClient.invalidateQueries({ queryKey: ['chatbot-subdomains'] });
        },
        onError: (e: any) => toast({
            variant: 'destructive', title: 'Erro ao verificar SSL',
            description: e.response?.data?.detail || e.message,
        }),
    });

    const filtered = (data || []).filter(s =>
        !search || s.subdomain.toLowerCase().includes(search.toLowerCase()) || (s.name || '').toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-semibold">Chatbots</h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Subdominios de chatbot vinculados a um dominio pai (herda os ad_units do pai)
                    </p>
                </div>
                <Button onClick={() => { setEditing(null); setFormOpen(true); }}>
                    <Plus className="w-4 h-4 mr-2" />Novo subdominio
                </Button>
            </div>

            <div className="mb-4 relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar subdominio..." className="pl-10" />
            </div>

            {isLoading ? (
                <div className="text-center py-12 text-slate-500">Carregando...</div>
            ) : filtered.length === 0 ? (
                <div className="border border-dashed rounded-lg p-12 text-center">
                    <p className="text-slate-500 mb-3">Nenhum subdominio cadastrado.</p>
                    <Button variant="outline" onClick={() => { setEditing(null); setFormOpen(true); }}>
                        <Plus className="w-4 h-4 mr-2" />Criar primeiro
                    </Button>
                </div>
            ) : (
                <div className="grid gap-3">
                    {filtered.map((s) => {
                        const isExpanded = expandedDns.has(s.id);
                        const needsConfig = s.ssl_status !== 'valid';
                        return (
                            <div key={s.id} className="bg-white border rounded-lg overflow-hidden hover:shadow-sm transition">
                                <div className="p-4 flex items-center justify-between cursor-pointer"
                                    onClick={() => navigate(`/chatbot/${s.id}`)}>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="font-semibold">{s.subdomain}</span>
                                            {sslBadge(s.ssl_status)}
                                            {!s.is_active && <Badge variant="secondary">inativo</Badge>}
                                        </div>
                                        <div className="text-xs text-slate-500 mt-1">
                                            {s.name && <span>{s.name} · </span>}
                                            pai: <span className="font-mono">{s.parent_domain?.domain || `domain_id=${s.parent_domain_id}`}</span>
                                            {s.ssl_expires_at && <span> · expira: {s.ssl_expires_at}</span>}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                        {needsConfig && (
                                            <Button variant="outline" size="sm" onClick={() => toggleDns(s.id)}
                                                className="text-amber-700 border-amber-300 hover:bg-amber-50">
                                                <HelpCircle className="w-3.5 h-3.5 mr-1" />
                                                {isExpanded ? 'Ocultar DNS' : 'Como configurar'}
                                                {isExpanded
                                                    ? <ChevronUp className="w-3 h-3 ml-1" />
                                                    : <ChevronDown className="w-3 h-3 ml-1" />}
                                            </Button>
                                        )}
                                        <Button variant="ghost" size="sm" onClick={() => sslMut.mutate(s.id)}
                                            disabled={sslMut.isPending} title="Re-verificar SSL">
                                            <RefreshCw className={`w-4 h-4 ${sslMut.isPending ? 'animate-spin' : ''}`} />
                                        </Button>
                                        <Button variant="ghost" size="sm" onClick={() => { setEditing(s); setFormOpen(true); }} title="Editar">
                                            Editar
                                        </Button>
                                        <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm(s)}
                                            className="text-red-600 hover:text-red-700" title="Deletar">
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                        <Button variant="ghost" size="sm" onClick={() => navigate(`/chatbot/${s.id}`)}>
                                            <ArrowRight className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>

                                {/* Bloco de DNS expandivel quando SSL nao esta valido */}
                                {needsConfig && isExpanded && (
                                    <div className="border-t bg-slate-50 p-4">
                                        <DnsInstructions subdomain={s} variant="inline" showStatus={false} />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <SubdomainFormModal
                open={formOpen}
                onOpenChange={setFormOpen}
                editing={editing}
            />

            <ConfirmDialog
                open={!!deleteConfirm}
                onOpenChange={(o) => !o && setDeleteConfirm(null)}
                title="Deletar subdominio?"
                description={`Isso vai remover ${deleteConfirm?.subdomain} e todos os chats/quizzes associados.`}
                confirmLabel="Deletar"
                onConfirm={() => deleteConfirm && deleteMut.mutate(deleteConfirm.id)}
                variant="danger"
            />
        </div>
    );
}
